import { supabase } from '@/lib/supabase';
import type { OrderStatus } from '@/types';
import type { OrderWithItems } from '@/store/seller';

/**
 * 매장의 모든 주문 목록을 가져옵니다 (상태 무관)
 */
export async function getAllOrdersForRestaurant(restaurantId: string): Promise<{
  success: boolean;
  data?: OrderWithItems[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('All orders fetch error:', error);
      throw error;
    }

    const ordersWithItems: OrderWithItems[] = data?.map(order => ({
      ...order,
      // Map database column names to expected interface
      buyer_nickname: order.users?.name || '사용자',
      // Use the new order_number field directly (database trigger ensures it's always present)
      order_number: order.order_number,
      items: order.order_items?.map((item: any) => ({
        ...item,
        // Legacy compatibility - map new schema to old field names
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || []
    })) || [];

    return {
      success: true,
      data: ordersWithItems
    };
  } catch (error) {
    console.error('All orders fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '모든 주문 목록을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 특정 상태의 주문 목록을 가져옵니다
 */
export async function getOrdersByStatus(restaurantId: string, status: OrderStatus): Promise<{
  success: boolean;
  data?: OrderWithItems[];
  error?: string;
}> {
  try {
    let query = supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('restaurant_id', restaurantId)
      .eq('status', status);
    
    // PENDING 상태일 때만 결제 완료/환불된 주문을 조회
    if (status === 'PENDING') {
      query = query.in('payment_status', ['PAID', 'REFUNDED']);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Orders fetch error:', error);
      throw error;
    }

    const ordersWithItems: OrderWithItems[] = data?.map(order => ({
      ...order,
      // Map database column names to expected interface
      buyer_nickname: order.users?.name || '사용자',
      // Use the new order_number field directly (database trigger ensures it's always present)
      order_number: order.order_number,
      items: order.order_items?.map((item: any) => ({
        ...item,
        // Legacy compatibility - map new schema to old field names
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || []
    })) || [];

    return {
      success: true,
      data: ordersWithItems
    };
  } catch (error) {
    console.error('Orders fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 목록을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 여러 상태의 주문 목록을 가져옵니다 (복합 상태 쿼리)
 */
export async function getMultipleStatusOrders(restaurantId: string, statuses: OrderStatus[]): Promise<{
  success: boolean;
  data?: OrderWithItems[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        cancellation_reason,
        auto_cancel_at,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('restaurant_id', restaurantId)
      .in('status', statuses)
      .in('payment_status', ['PAID', 'REFUNDED'])  // 결제 완료/환불된 주문 조회
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Multiple status orders fetch error:', error);
      throw error;
    }

    const ordersWithItems: OrderWithItems[] = data?.map(order => ({
      ...order,
      buyer_nickname: order.users?.name || '사용자',
      order_number: order.order_number,
      items: order.order_items?.map((item: any) => ({
        ...item,
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || []
    })) || [];

    return {
      success: true,
      data: ordersWithItems
    };
  } catch (error) {
    console.error('Multiple status orders fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 목록을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 주문 상세 정보를 가져옵니다
 */
export async function getOrderDetail(orderId: string): Promise<{
  success: boolean;
  data?: OrderWithItems;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        restaurants (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('id', orderId)
      .single();

    if (error) {
      console.error('Order detail fetch error:', error);
      throw error;
    }

    const orderWithItems: OrderWithItems = {
      ...data,
      // Map database column names to expected interface
      buyer_nickname: data.users?.name || '사용자',
      // Use the new order_number field directly (database trigger ensures it's always present)
      order_number: data.order_number,
      items: data.order_items?.map((item: any) => ({
        ...item,
        // Legacy compatibility - map new schema to old field names
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || [],
      restaurant: data.restaurants
    };

    return {
      success: true,
      data: orderWithItems
    };
  } catch (error) {
    console.error('Order detail fetch error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 상세 정보를 불러오는데 실패했습니다'
    };
  }
}

/**
 * 주문번호로 주문을 조회합니다 (판매자용)
 */
export async function getOrderByNumber(restaurantId: string, orderNumber: string): Promise<{
  success: boolean;
  data?: OrderWithItems;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        restaurants (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('restaurant_id', restaurantId)
      .eq('order_number', orderNumber)
      .single();

    if (error) {
      console.error('Order by number fetch error:', error);
      return {
        success: false,
        error: '해당 주문번호를 찾을 수 없습니다.'
      };
    }

    const orderWithItems: OrderWithItems = {
      ...data,
      // Map database column names to expected interface
      buyer_nickname: data.users?.name || '사용자',
      order_number: data.order_number,
      items: data.order_items?.map((item: any) => ({
        ...item,
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || [],
      restaurant: data.restaurants
    };

    return {
      success: true,
      data: orderWithItems
    };
  } catch (error) {
    console.error('Get order by number error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문번호 조회에 실패했습니다'
    };
  }
}

/**
 * 주문번호로 검색합니다 (부분 일치, 판매자용)
 */
export async function searchOrdersByNumber(restaurantId: string, searchTerm: string, limit: number = 10): Promise<{
  success: boolean;
  data?: OrderWithItems[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          products (
            name
          )
        ),
        users (
          name
        ),
        payment:payments(
          approved_at,
          paid_at,
          status
        )
      `)
      .eq('restaurant_id', restaurantId)
      .ilike('order_number', `%${searchTerm}%`)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Search orders by number error:', error);
      throw error;
    }

    const ordersWithItems: OrderWithItems[] = data?.map(order => ({
      ...order,
      buyer_nickname: order.users?.name || '사용자',
      order_number: order.order_number,
      items: order.order_items?.map((item: any) => ({
        ...item,
        product_name: item.products?.name || '상품',
        price: item.unit_price || 0
      })) || []
    })) || [];

    return {
      success: true,
      data: ordersWithItems
    };
  } catch (error) {
    console.error('Search orders by number error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문번호 검색에 실패했습니다'
    };
  }
}
